import React, { useEffect, useRef } from 'react';

import veyloLockup from '../../assets/brand/veylo-wordmark-black.svg';
import { useTheme } from '../../contexts/ThemeContext';
import type { IBrandIntroProps } from './BrandIntro.types';
import {
  BRAND_INTRO_DURATION,
  mountBrandIntro,
  type IBrandIntroHandle,
  type IBrandIntroTheme,
} from './brandIntroEngine';

/**
 * The piece's own ink, one colour per theme — the whole field, its links, the
 * mark and the wordmark are drawn in it, and only their opacity differs. These
 * are the values the composition was designed against rather than page tokens:
 * the field is a picture of a system being built, and a second hue in it reads
 * as a legend the picture does not have.
 */
const INK: Record<'light' | 'dark', string> = {
  light: '#201e1d',
  dark: '#eae9e6',
};

const readTheme = (mode: 'light' | 'dark'): IBrandIntroTheme => ({ ink: INK[mode] });

export const BrandIntro: React.FC<IBrandIntroProps> = ({
  duration = BRAND_INTRO_DURATION,
  loop = false,
  gap = 0,
  stage = 1,
  focusX = 0.5,
  clearWidth = 1,
  still = false,
  className = '',
}: IBrandIntroProps) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<IBrandIntroHandle | null>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const reducedMotion =
      still ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    const handle = mountBrandIntro(host, {
      lockupSrc: veyloLockup,
      theme: readTheme(theme),
      duration,
      loop,
      gap,
      stage,
      focusX,
      clearWidth,
      reducedMotion,
    });
    handleRef.current = handle;

    return () => {
      handle.destroy();
      handleRef.current = null;
    };
    // `theme` is deliberately absent: it only seeds the first paint, and the
    // effect below recolours in place rather than rebuilding the field and
    // restarting the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, loop, gap, stage, focusX, clearWidth, still]);

  useEffect(() => {
    handleRef.current?.setTheme(readTheme(theme));
  }, [theme]);

  return <div ref={hostRef} aria-hidden="true" className={`w-full h-full ${className}`} />;
};
